/**
 * CO landing — today's operating run.
 * Inbox = unfinished work. Calendar = when work is expected, plus CO meetings / follow-ups.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronRightIcon } from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../config/api'
import PatronTypeahead, { selectedPatronPayload } from './components/PatronTypeahead'

const C = {
  pri: '#0a5169',
  priL: '#1a7a9b',
  priM: '#e8f2f6',
  red: '#dc2626',
  redBg: '#fef2f2',
  amber: '#d97706',
  amberBg: '#fffbeb',
  green: '#16a34a',
  slate: '#64748b',
  slateL: '#94a3b8',
  slateBg: '#f8fafc',
  border: '#e2e8f0',
  ink: '#0f172a',
}

function isoDate(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function parseIso(iso) {
  return new Date(`${iso}T12:00:00`)
}

function fmtDay(d) {
  return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })
}

function fmtLong(d) {
  return d.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
}

function currentFyId() {
  const now = new Date()
  const y = now.getFullYear()
  const start = now.getMonth() >= 6 ? y : y - 1
  return `${start}-${String(start + 1).slice(-2)}`
}

function jsonOk(res) {
  return res.json().then((j) => (res.ok && j?.success ? j : null)).catch(() => null)
}

function apiEventToUi(e) {
  return {
    id: e.id,
    date: e.event_date,
    kind: e.kind === 'meeting' ? 'meeting' : 'task',
    title: e.title,
    detail: e.notes || '',
    time: e.event_time || '',
    done: e.status === 'done' || e.status === 'cancelled',
    patronName: e.patron_name || '',
    memberId: e.member_id || '',
    masterProfileId: e.master_profile_id || '',
  }
}

function daysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate()
}

function routineEventsForMonth(year, month) {
  const last = daysInMonth(year, month)
  const list = []
  for (let d = 1; d <= last; d++) {
    const date = new Date(year, month, d)
    if (date.getDay() === 5) {
      list.push({
        id: `weekly-${isoDate(date)}`,
        date: isoDate(date),
        kind: 'routine',
        title: 'Weekly session',
        detail: 'TTR, checklists, overdue review',
      })
    }
  }
  const monthly = new Date(year, month, Math.min(25, last))
  list.push({
    id: `monthly-${isoDate(monthly)}`,
    date: isoDate(monthly),
    kind: 'routine',
    title: 'Monthly session',
    detail: 'Rule performance, ECDD aging, risk-score drift',
  })
  return list
}

function obligationMilestones(items) {
  return (items || []).flatMap((o) => {
    const date = o.dueDate || o.due_date || o.nextDue || null
    if (!date) return []
    const iso = String(date).slice(0, 10)
    return [{
      id: `obl-${o.id || o.obligation_id || iso}-${o.title || o.name}`,
      date: iso,
      kind: 'mile',
      title: o.title || o.name || 'Obligation due',
      detail: o.status === 'overdue' ? 'Overdue' : o.status === 'due_soon' ? 'Due soon' : 'Milestone',
    }]
  })
}

export default function CoOperatingRun() {
  const navigate = useNavigate()
  const [filter, setFilter] = useState('all')
  const [alsoOpen, setAlsoOpen] = useState(false)
  const [doneOpen, setDoneOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const [ops, setOps] = useState(null)
  const [listings, setListings] = useState(null)
  const [obligationItems, setObligationItems] = useState([])
  const [incidentsOpen, setIncidentsOpen] = useState(0)
  const [userEvents, setUserEvents] = useState([])
  const [calMonth, setCalMonth] = useState(() => {
    const n = new Date()
    return { year: n.getFullYear(), month: n.getMonth() }
  })
  const [selectedDay, setSelectedDay] = useState(() => isoDate(new Date()))
  const [panel, setPanel] = useState(null)
  const [form, setForm] = useState({ what: '', date: isoDate(new Date()), time: '14:00', notes: '' })
  const [selectedPatron, setSelectedPatron] = useState(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  const today = useMemo(() => new Date(), [])
  const todayIso = isoDate(today)
  const yest = useMemo(() => {
    const d = new Date()
    d.setDate(d.getDate() - 1)
    return d
  }, [])

  const loadUserEvents = useCallback(async () => {
    const start = new Date(calMonth.year, calMonth.month, 1)
    const end = new Date(calMonth.year, calMonth.month + 1, 0)
    const from = isoDate(start)
    const to = isoDate(end)
    const j = await authFetch(getLocalApiUrl(`/api/compliance/operating-events?from=${from}&to=${to}`)).then(jsonOk)
    setUserEvents((j?.data || []).map(apiEventToUi))
  }, [calMonth])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      const from = isoDate(yest)
      const to = isoDate(today)
      const [opsJ, listJ, govJ, incJ] = await Promise.all([
        authFetch(getLocalApiUrl('/api/compliance/operations-summary?period=today')).then(jsonOk),
        authFetch(getLocalApiUrl(`/api/compliance/period-listings/summary?dateFrom=${from}&dateTo=${to}`)).then(jsonOk),
        authFetch(getLocalApiUrl(`/api/governance/dashboard?year=${encodeURIComponent(currentFyId())}`)).then(jsonOk),
        authFetch(getLocalApiUrl('/api/mobile/incidents?limit=80')).then(jsonOk),
      ])
      if (cancelled) return
      setOps(opsJ?.data || null)
      setListings(listJ?.data || null)
      const obs = [
        ...(govJ?.data?.obligations || []),
      ]
      setObligationItems(obs.filter((o) => o.status === 'overdue' || o.status === 'due_soon'))
      const incs = Array.isArray(incJ?.data) ? incJ.data : []
      setIncidentsOpen(incs.filter((i) => {
        const s = String(i.status || i.workflow_status || '').toLowerCase()
        return s && !['closed', 'resolved', 'dismissed', 'no_incident'].includes(s)
      }).length || incs.length)
      setLoading(false)
    })()
    return () => { cancelled = true }
  }, [today, yest])

  useEffect(() => { loadUserEvents() }, [loadUserEvents])

  const stats = ops?.stats || {}
  const kpis = ops?.kpis || {}
  const smr = ops?.smrDeadlines || []
  const overdueObl = obligationItems.filter((o) => o.status === 'overdue')
  const dueSoonObl = obligationItems.filter((o) => o.status === 'due_soon')
  const oblPending = obligationItems.length

  const minePending = useMemo(
    () => userEvents.filter((e) => (e.kind === 'meeting' || e.kind === 'task') && e.date && e.date <= todayIso && !e.done),
    [userEvents, todayIso],
  )

  const rows = useMemo(() => {
    const alerts = Number(stats.alertsUnreviewed ?? stats.alertsOpen ?? 0)
    const payouts = Number(stats.payoutsInPeriod ?? stats.payoutsToday ?? 0)
    const pep = Number(listings?.pepDetections ?? 0)
    const large = Number(listings?.largeNonCashPayouts ?? 0)
    const repeats = Number(listings?.repeatLargePayees ?? 0)
    const highRisk = Number(listings?.highRiskAdded ?? 0)
    const kyc = Number(stats.kycRenewalsDue ?? 0)
    const smrLeft = smr.length
    const smrOver = Number(kpis.smrOverdue ?? smr.filter((s) => s.urgency === 'overdue').length)
    const list = [
      oblPending > 0 && {
        id: 'obligations',
        band: 'now',
        window: 'clock',
        heat: overdueObl.length ? 'red' : 'amber',
        title: 'Obligation gaps',
        asof: overdueObl.length
          ? `${overdueObl.length} overdue · ${dueSoonObl.length} due soon`
          : `${dueSoonObl.length} due soon`,
        hint: 'Program register — unfinished AUSTRAC / AML obligations',
        pending: oblPending,
        href: '/governance-safe/obligations',
      },
      smrLeft > 0 && {
        id: 'smr',
        band: smrOver > 0 ? 'now' : 'also',
        window: 'clock',
        heat: smrOver > 0 ? 'red' : 'amber',
        title: 'SMR clock',
        asof: smrOver > 0 ? `${smrOver} overdue filing` : 'Suspicion formed · 3-day clock',
        hint: `${smrLeft} open without SMR filed`,
        pending: smrLeft,
        href: '/compliance-safe/investigations/pipeline',
      },
      payouts > 0 && {
        id: 'payouts',
        band: 'now',
        window: 'mixed',
        heat: 'amber',
        title: 'Payouts',
        asof: `${fmtDay(yest)} + ${fmtDay(today)} live`,
        hint: 'Today’s payouts on the list — leftover from last night included when still open',
        pending: payouts,
        href: '/payout-safe/monitoring',
      },
      alerts > 0 && {
        id: 'alerts',
        band: 'now',
        window: 'yest',
        heat: alerts >= 10 ? 'amber' : '',
        title: 'Monitoring alerts',
        asof: `${fmtDay(yest)} trading day`,
        hint: 'Unreviewed · usually yesterday after overnight import',
        pending: alerts,
        href: '/monitor-safe/alerts',
      },
      incidentsOpen > 0 && {
        id: 'uar',
        band: 'now',
        window: 'live',
        heat: '',
        title: 'UAR / incidents',
        asof: `${fmtDay(today)} live`,
        hint: 'Floor reports still open',
        pending: incidentsOpen,
        href: '/compliance-safe/incidents',
      },
      pep > 0 && {
        id: 'pep',
        band: 'now',
        window: 'mixed',
        heat: '',
        title: 'PEP — Action',
        asof: `${fmtDay(yest)} · ${fmtDay(today)}`,
        hint: 'CO chooses the action — not auto ECDD',
        pending: pep,
        href: '/compliance-safe/period-reports?tab=pep',
      },
      minePending.length > 0 && {
        id: 'mine',
        band: 'now',
        window: 'live',
        heat: 'amber',
        title: 'My meetings & follow-ups',
        asof: 'Today + leftover',
        hint: 'Added on this page — stays until you clear it',
        pending: minePending.length,
        href: null,
      },
      large > 0 && {
        id: 'eft',
        band: 'also',
        window: 'mixed',
        heat: '',
        title: 'Large EFT / cheques',
        asof: 'Last night + today · ≥ $5,000',
        hint: 'Non-cash payouts in the window',
        pending: large,
        href: '/compliance-safe/period-reports?tab=large',
      },
      repeats > 0 && {
        id: 'repeat',
        band: 'also',
        window: 'mixed',
        heat: '',
        title: 'Repeat large payees',
        asof: 'Period window',
        hint: '2+ large payouts',
        pending: repeats,
        href: '/compliance-safe/period-reports?tab=repeat',
      },
      highRisk > 0 && {
        id: 'hr',
        band: 'also',
        window: 'yest',
        heat: '',
        title: 'High-risk added',
        asof: `${fmtDay(yest)} trading day`,
        hint: 'CDD moved to enhanced',
        pending: highRisk,
        href: '/compliance-safe/period-reports?tab=highrisk',
      },
      kyc > 0 && {
        id: 'kyc',
        band: 'also',
        window: 'live',
        heat: '',
        title: 'KYC due',
        asof: 'Live / leftover',
        hint: 'Renewals still open',
        pending: kyc,
        href: '/compliance-safe/kyc-registry',
      },
    ]
    return list.filter(Boolean)
  }, [stats, listings, incidentsOpen, oblPending, overdueObl.length, dueSoonObl.length, smr, kpis, today, yest, minePending.length])

  const match = (row) => {
    if (filter === 'all') return true
    if (filter === 'yest') return row.window === 'yest' || row.window === 'mixed'
    if (filter === 'live') return row.window === 'live' || row.window === 'mixed'
    if (filter === 'clock') return row.window === 'clock'
    return true
  }

  const nowRows = rows.filter((r) => r.band === 'now' && r.pending > 0 && match(r))
  const alsoRows = rows.filter((r) => r.band === 'also' && r.pending > 0 && match(r))
  const nowCount = nowRows.reduce((s, r) => s + r.pending, 0)
  const alsoCount = alsoRows.reduce((s, r) => s + r.pending, 0)
  const weekday = today.toLocaleDateString('en-AU', { weekday: 'long' })
  const isWeeklyDay = today.getDay() === 5

  const calendarEvents = useMemo(() => {
    const routine = routineEventsForMonth(calMonth.year, calMonth.month)
    const miles = obligationMilestones(obligationItems)
    return [...routine, ...miles, ...userEvents.filter((e) => !e.done)]
  }, [calMonth, obligationItems, userEvents])

  const eventsOn = (date) => calendarEvents.filter((e) => e.date === date)
  const selectedEvents = eventsOn(selectedDay)
  const selectedLabel = parseIso(selectedDay).toLocaleDateString('en-AU', {
    weekday: 'long', day: 'numeric', month: 'short',
  })

  const openForm = (kind) => {
    setSaveError('')
    setSelectedPatron(null)
    setForm({
      what: '',
      date: selectedDay,
      time: kind === 'meeting' ? '14:00' : '09:30',
      notes: '',
    })
    setPanel({ type: 'form', kind })
  }

  const onRowAct = (row) => {
    if (row.id === 'mine') {
      setPanel({ type: 'mine' })
      return
    }
    if (row.href) navigate(row.href)
  }

  const saveEvent = async () => {
    const kind = panel?.kind === 'meeting' ? 'meeting' : 'followup'
    const patron = selectedPatronPayload(selectedPatron)
    const purpose = form.what.trim()
    const title = patron.patron_name
      ? (purpose ? `${patron.patron_name} · ${purpose}` : patron.patron_name)
      : (purpose || (kind === 'meeting' ? 'Meeting' : 'Follow-up'))
    const date = form.date || todayIso
    const time = form.time || ''
    const notes = form.notes.trim()
    setSaving(true)
    setSaveError('')
    const j = await authFetch(getLocalApiUrl('/api/compliance/operating-events'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        kind,
        title,
        notes,
        event_date: date,
        event_time: time,
        ...patron,
      }),
    }).then(jsonOk)
    setSaving(false)
    if (!j?.data) {
      setSaveError('Could not save to the tenant database. Try again.')
      return
    }
    setUserEvents((prev) => [...prev, apiEventToUi(j.data)])
    setSelectedDay(date)
    const d = parseIso(date)
    setCalMonth({ year: d.getFullYear(), month: d.getMonth() })
    setPanel(null)
  }

  const markDone = async (ev) => {
    const j = await authFetch(getLocalApiUrl(`/api/compliance/operating-events/${encodeURIComponent(ev.id)}`), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: 'done' }),
    }).then(jsonOk)
    if (!j?.data) return
    setUserEvents((prev) => prev.map((e) => (e.id === ev.id ? apiEventToUi(j.data) : e)))
  }

  const shiftMonth = (delta) => {
    setCalMonth((prev) => {
      const d = new Date(prev.year, prev.month + delta, 1)
      return { year: d.getFullYear(), month: d.getMonth() }
    })
  }

  return (
    <div style={{ paddingBottom: 32, fontFamily: "'Poppins', system-ui, sans-serif" }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 4px', color: C.ink }}>Today’s operating run</h1>
          <p style={{ fontSize: 13, color: C.slate, margin: 0 }}>
            Unfinished work only · {fmtLong(today)}
          </p>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
            <span style={pill(true)}>Daily</span>
            <span style={pill(isWeeklyDay, isWeeklyDay)}>{isWeeklyDay ? 'Weekly day' : `${weekday} · weekly leftover still shows`}</span>
            <span style={pill(false)}>Monthly window</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" style={ghostBtn} onClick={() => openForm('meeting')}>+ Meeting</button>
          <button type="button" style={ghostBtn} onClick={() => openForm('task')}>+ Follow-up</button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px] gap-5 items-start">
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 14 }}>
            <Stat n={loading ? '—' : nowCount} label="Need you now" hot />
            <Stat n={loading ? '—' : alsoCount} label="Also open" />
            <Stat n={oblPending} label="Obligation gaps" hot={overdueObl.length > 0} />
          </div>

          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
            {[
              ['all', 'All unfinished'],
              ['yest', 'Yesterday’s trading day'],
              ['live', 'Live today'],
              ['clock', 'Clocks'],
            ].map(([id, label]) => (
              <button key={id} type="button" onClick={() => setFilter(id)} style={filterBtn(filter === id)}>
                {label}
              </button>
            ))}
          </div>

          <div style={band}>Needs you now</div>
          <div style={listBox}>
            {loading && <div style={{ padding: 16, fontSize: 13, color: C.slate }}>Loading today’s run…</div>}
            {!loading && nowRows.length === 0 && (
              <div style={{ padding: 16, fontSize: 13, color: C.slate }}>Nothing in this filter.</div>
            )}
            {nowRows.map((row) => (
              <Row key={row.id} row={row} onAct={() => onRowAct(row)} />
            ))}
          </div>

          <button type="button" onClick={() => setAlsoOpen((v) => !v)} style={disclose}>
            Also open · {alsoRows.length} quieter item{alsoRows.length === 1 ? '' : 's'}
          </button>
          {alsoOpen && (
            <div style={{ ...listBox, marginTop: 8 }}>
              {alsoRows.length === 0 && <div style={{ padding: 16, fontSize: 13, color: C.slate }}>Nothing quieter right now.</div>}
              {alsoRows.map((row) => (
                <Row key={row.id} row={row} onAct={() => onRowAct(row)} />
              ))}
            </div>
          )}

          <button type="button" onClick={() => setDoneOpen((v) => !v)} style={disclose}>
            Finished / current · obligations on track stay off this list
          </button>
          {doneOpen && (
            <div style={{ ...listBox, marginTop: 8, padding: 14, fontSize: 13, color: C.slate }}>
              Rows leave the run when pending is zero. Current obligations (no gap) are not listed — only overdue and due-soon gaps.
              Meetings and follow-ups stay until you mark them done.
            </div>
          )}
        </div>

        <aside style={calBox}>
          <h2 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 800, color: C.ink }}>Operations calendar</h2>
          <p style={{ fontSize: 12, color: C.slate, margin: '0 0 12px' }}>
            When work is expected — not a second inbox. Routine sessions, clocks, meetings, follow-ups.
          </p>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, fontSize: 13, fontWeight: 700 }}>
            <button type="button" style={calNav} onClick={() => shiftMonth(-1)} aria-label="Previous month">‹</button>
            <span>
              {new Date(calMonth.year, calMonth.month, 1).toLocaleDateString('en-AU', { month: 'long', year: 'numeric' })}
            </span>
            <button type="button" style={calNav} onClick={() => shiftMonth(1)} aria-label="Next month">›</button>
          </div>
          <MonthGrid
            year={calMonth.year}
            month={calMonth.month}
            todayIso={todayIso}
            selectedDay={selectedDay}
            events={calendarEvents}
            onSelect={setSelectedDay}
          />
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 11, color: C.slate, marginTop: 10 }}>
            <LegendDot color={C.priL} label="Routine" />
            <LegendDot color={C.amber} label="Meeting" />
            <LegendDot color={C.green} label="Follow-up" />
            <LegendDot color={C.red} label="Milestone" />
          </div>
          <div style={{ marginTop: 12, borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 700 }}>{selectedLabel}</h3>
            {selectedEvents.length === 0 && (
              <p style={{ fontSize: 13, color: C.slate, margin: 0 }}>Nothing expected this day.</p>
            )}
            {selectedEvents.map((e) => (
              <div key={e.id} style={{ fontSize: 12, padding: '6px 0', borderBottom: `1px solid ${C.border}` }}>
                <b style={{ display: 'block', color: C.ink }}>{e.title}</b>
                <span style={{ color: C.slate }}>
                  {e.time ? `${e.time} · ` : ''}
                  {e.memberId ? `${e.memberId} · ` : ''}
                  {e.detail || e.kind}
                </span>
              </div>
            ))}
          </div>
        </aside>
      </div>

      {panel && (
        <div
          role="presentation"
          onClick={() => setPanel(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.35)', display: 'flex', justifyContent: 'flex-end', zIndex: 40 }}
        >
          <div
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(520px, 100%)',
              background: '#fff',
              height: '100%',
              overflow: 'auto',
              padding: '18px 18px 40px',
              borderLeft: `1px solid ${C.border}`,
            }}
          >
            <button type="button" onClick={() => setPanel(null)} style={backBtn}>← Back to run</button>
            {panel.type === 'form' && (
              <>
                <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 800 }}>
                  {panel.kind === 'meeting' ? 'Add meeting' : 'Add follow-up'}
                </h2>
                <p style={{ fontSize: 12, color: C.slate, margin: '0 0 14px' }}>
                  Saved on this run and on the operations calendar. Look up a patron by name or ID, then add what the meeting is for.
                </p>
                <Field label="Patron">
                  <PatronTypeahead value={selectedPatron} onChange={setSelectedPatron} inputStyle={input} />
                </Field>
                <Field label="What">
                  <input
                    value={form.what}
                    onChange={(e) => setForm((f) => ({ ...f, what: e.target.value }))}
                    placeholder={panel.kind === 'meeting' ? 'SoW interview' : 'Call security on CCTV pull'}
                    style={input}
                  />
                </Field>
                <Field label="Date">
                  <input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} style={input} />
                </Field>
                <Field label="Time">
                  <input type="time" value={form.time} onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))} style={input} />
                </Field>
                <Field label="Notes">
                  <textarea
                    rows={3}
                    value={form.notes}
                    onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                    placeholder="Venue, who, why"
                    style={input}
                  />
                </Field>
                {saveError && <p style={{ color: C.red, fontSize: 12 }}>{saveError}</p>}
                <button type="button" disabled={saving} onClick={saveEvent} style={saveBtn}>
                  {saving ? 'Saving…' : 'Save to calendar'}
                </button>
              </>
            )}
            {panel.type === 'mine' && (
              <>
                <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 800 }}>My meetings & follow-ups</h2>
                <p style={{ fontSize: 12, color: C.slate, margin: '0 0 14px' }}>
                  Due today or leftover — stays until you mark it done.
                </p>
                {minePending.length === 0 && (
                  <p style={{ fontSize: 13, color: C.slate }}>Nothing left on this list.</p>
                )}
                {minePending.map((e) => (
                  <div key={e.id} style={itemBox}>
                    <div>
                      <b style={{ display: 'block', fontSize: 13 }}>{e.title}</b>
                      <span style={{ fontSize: 12, color: C.slate }}>
                        {e.time ? `${e.time} · ` : ''}
                        {e.memberId ? `${e.memberId} · ` : ''}
                        {e.detail || e.date}
                      </span>
                    </div>
                    <button type="button" onClick={() => markDone(e)} style={saveBtn}>Mark done</button>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function MonthGrid({ year, month, todayIso, selectedDay, events, onSelect }) {
  const first = new Date(year, month, 1)
  const startPad = first.getDay()
  const last = daysInMonth(year, month)
  const cells = []
  for (let i = 0; i < startPad; i++) cells.push({ off: true, key: `pad-${i}` })
  for (let d = 1; d <= last; d++) {
    const date = isoDate(new Date(year, month, d))
    const kinds = [...new Set(events.filter((e) => e.date === date).map((e) => e.kind))]
    cells.push({ key: date, date, day: d, kinds })
  }
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
      {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((w, i) => (
        <div key={`${w}-${i}`} style={{ fontSize: 10, fontWeight: 700, color: C.slateL, textAlign: 'center', padding: '4px 0' }}>{w}</div>
      ))}
      {cells.map((cell) => (
        cell.off ? (
          <button key={cell.key} type="button" style={{ ...dayBtn, visibility: 'hidden' }} tabIndex={-1} />
        ) : (
          <button
            key={cell.key}
            type="button"
            onClick={() => onSelect(cell.date)}
            style={{
              ...dayBtn,
              background: cell.date === selectedDay ? '#d7ebf2' : cell.date === todayIso ? C.priM : C.slateBg,
              outline: cell.date === todayIso ? `1px solid ${C.pri}` : 'none',
            }}
          >
            <span style={{ fontSize: 11, fontWeight: 700, display: 'block' }}>{cell.day}</span>
            <span style={{ display: 'flex', gap: 2, flexWrap: 'wrap', marginTop: 3, minHeight: 6 }}>
              {cell.kinds.map((k) => (
                <i
                  key={k}
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    display: 'block',
                    background: k === 'meeting' ? C.amber : k === 'task' ? C.green : k === 'mile' ? C.red : C.priL,
                  }}
                />
              ))}
            </span>
          </button>
        )
      ))}
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.slate, marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  )
}

function LegendDot({ color, label }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <i style={{ width: 7, height: 7, borderRadius: '50%', display: 'inline-block', background: color }} />
      {label}
    </span>
  )
}

function Row({ row, onAct }) {
  const bg = row.heat === 'red' ? '#fff8f8' : row.heat === 'amber' ? '#fffdf6' : '#fff'
  const dot = row.heat === 'red' ? C.red : row.heat === 'amber' ? C.amber : C.slateL
  return (
    <button
      type="button"
      aria-label={`Open ${row.title}`}
      onClick={onAct}
      style={{
        display: 'grid',
        gridTemplateColumns: '28px 1fr 56px 28px',
        gap: 10,
        alignItems: 'center',
        padding: '12px 14px',
        border: 0,
        borderTop: `1px solid ${C.border}`,
        width: '100%',
        textAlign: 'left',
        background: bg,
        cursor: 'pointer',
        fontFamily: 'inherit',
      }}
    >
      <i style={{ width: 10, height: 10, borderRadius: '50%', background: dot, display: 'block' }} />
      <div>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>{row.title}</div>
        <div style={{ fontSize: 12, color: C.slate, marginTop: 2 }}>{row.asof} · {row.hint}</div>
      </div>
      <div style={{ fontSize: 20, fontWeight: 800, textAlign: 'right', color: row.heat === 'red' ? C.red : C.ink }}>
        {row.pending}
      </div>
      <ChevronRightIcon
        aria-hidden
        style={{ width: 18, height: 18, color: C.slateL, justifySelf: 'end' }}
      />
    </button>
  )
}

function Stat({ n, label, hot }) {
  return (
    <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10, padding: '10px 12px' }}>
      <b style={{ display: 'block', fontSize: 22, fontWeight: 800, lineHeight: 1.1, color: hot ? C.red : C.ink }}>{n}</b>
      <span style={{ fontSize: 12, color: C.slate }}>{label}</span>
    </div>
  )
}

function pill(on, warn) {
  return {
    fontSize: 11,
    fontWeight: 700,
    padding: '4px 10px',
    borderRadius: 20,
    border: `1px solid ${on ? C.pri : warn ? '#fde68a' : C.border}`,
    background: on ? C.pri : warn ? C.amberBg : '#fff',
    color: on ? '#fff' : warn ? '#92400e' : C.pri,
  }
}

function filterBtn(active) {
  return {
    border: `1px solid ${active ? C.pri : '#b6d4de'}`,
    background: active ? C.pri : '#fff',
    color: active ? '#fff' : C.pri,
    borderRadius: 8,
    padding: '6px 12px',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
  }
}

const band = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.6,
  textTransform: 'uppercase',
  color: C.slateL,
  margin: '18px 0 8px',
}

const listBox = {
  background: '#fff',
  border: `1px solid ${C.border}`,
  borderRadius: 10,
  overflow: 'hidden',
}

const disclose = {
  width: '100%',
  textAlign: 'left',
  background: '#fff',
  border: `1px solid ${C.border}`,
  borderRadius: 10,
  padding: '10px 14px',
  fontSize: 13,
  fontWeight: 700,
  color: C.pri,
  cursor: 'pointer',
  marginTop: 8,
  fontFamily: 'inherit',
}

const ghostBtn = {
  border: '1px solid #b6d4de',
  background: '#fff',
  color: C.pri,
  borderRadius: 8,
  padding: '7px 12px',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'inherit',
}

const calBox = {
  background: '#fff',
  border: `1px solid ${C.border}`,
  borderRadius: 10,
  padding: 14,
  position: 'sticky',
  top: 12,
}

const calNav = {
  border: `1px solid ${C.border}`,
  background: '#fff',
  color: C.pri,
  borderRadius: 6,
  width: 28,
  height: 28,
  cursor: 'pointer',
  fontWeight: 700,
  fontFamily: 'inherit',
}

const dayBtn = {
  minHeight: 44,
  border: 0,
  borderRadius: 6,
  padding: '4px 3px 6px',
  cursor: 'pointer',
  textAlign: 'left',
  color: C.ink,
  fontFamily: 'inherit',
}

const backBtn = {
  background: 'none',
  border: 0,
  color: C.pri,
  fontWeight: 700,
  fontSize: 13,
  padding: 0,
  marginBottom: 10,
  cursor: 'pointer',
  fontFamily: 'inherit',
}

const input = {
  width: '100%',
  border: `1px solid ${C.border}`,
  borderRadius: 8,
  padding: '8px 10px',
  fontSize: 13,
  fontFamily: 'inherit',
}

const saveBtn = {
  flexShrink: 0,
  border: '1px solid #b6d4de',
  background: C.pri,
  color: '#fff',
  borderRadius: 8,
  padding: '6px 10px',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'inherit',
}

const itemBox = {
  border: `1px solid ${C.border}`,
  borderRadius: 8,
  padding: '10px 12px',
  marginBottom: 8,
  display: 'flex',
  justifyContent: 'space-between',
  gap: 10,
  alignItems: 'flex-start',
}
